/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import { compose } from 'recompose';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import user from 'js/lib/user';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { Typography } from '@coursera/cds-core';

import { COURSERA_FOR_CAMPUS_BASIC_SETUP_PATH } from 'bundles/enterprise-admin-coursera-for-campus-basic/constants';
import type { FirstNameQuery as FirstNameQueryType } from 'bundles/enterprise-admin-coursera-for-campus-basic/utils/__generated__/FirstNameQuery';
import type {
  ProgramDraftQuery as ProgramDraftQueryData,
  ProgramDraftQueryVariables,
} from 'bundles/enterprise-admin-coursera-for-campus-basic/utils/__generated__/ProgramDraftQuery';
import { org_coursera_program_draft_ProgramDraftStatus as ProgramDraftStatus } from 'bundles/enterprise-admin-coursera-for-campus-basic/utils/__generated__/globalTypes';
import { FirstNameQuery, ProgramDraftQuery } from 'bundles/enterprise-admin-coursera-for-campus-basic/utils/queries';
import { PersonallyIdentifiableInfo } from 'bundles/enterprise-ui';
import WelcomeBanner from 'bundles/enterprise-ui/components/WelcomeBanner';

import _t from 'i18n!nls/enterprise-admin-coursera-for-campus-basic';

type PropsFomCaller = {
  orgSlug: string;
  numOfPrograms: number;
  programSlug: string;
  programId: string;
};

type PropsFromFirstNameQuery = {
  firstName?: string;
};

type PropsFromProgramInvitationsQuery = {
  hasLearners: boolean;
};

export type Props = PropsFomCaller & PropsFromFirstNameQuery & PropsFromProgramInvitationsQuery;

const descriptionContainer = css({
  maxWidth: '800px',
});

export const ContinueProgramSetUpBanner: React.FC<Props> = ({
  firstName,
  programSlug,
  orgSlug,
  numOfPrograms,
  hasLearners,
}) => {
  const shouldRenderContinueProgramSetupBanner = numOfPrograms === 1 && !hasLearners;

  if (shouldRenderContinueProgramSetupBanner) {
    return (
      <WelcomeBanner
        orgSlug={orgSlug}
        buttonText={_t('Continue program set up')}
        buttonTrackingName="continue_program_setup"
        buttonURL={`/o/${orgSlug}/admin/programs/${programSlug}/${COURSERA_FOR_CAMPUS_BASIC_SETUP_PATH}`}
        buttonAriaLabel={_t('Continue program set up')}
        data-e2e="continue-program-setup-banner"
        headerText={
          firstName ? (
            <PersonallyIdentifiableInfo>
              <FormattedMessage message={_t('Welcome, {name}!')} name={firstName} />
            </PersonallyIdentifiableInfo>
          ) : (
            <FormattedMessage message={_t('Welcome!')} data-e2e="no-name-welcome" />
          )
        }
      >
        <>
          <Typography variant="h1" component="p">
            {_t('Invite your students to become learners on Coursera today!')}
          </Typography>
          <Typography variant="h1" component="p">
            {_t('Continue setting up your learning program in a few quick steps.')}
          </Typography>
        </>
      </WelcomeBanner>
    );
  }

  return (
    <WelcomeBanner
      orgSlug={orgSlug}
      buttonText={_t('Get started in 3 easy steps')}
      buttonTrackingName="go_to_freemium_program_setup"
      buttonURL={`/o/${orgSlug}/admin/programs/freemium-program-setup`}
      buttonAriaLabel={_t('Go to freemium program setup')}
      data-e2e="new-program-setup-banner"
      headerText={
        firstName ? (
          <FormattedMessage message={_t('Welcome back, {name}!')} name={firstName} />
        ) : (
          <FormattedMessage message={_t('Welcome back!')} data-e2e="no-name-welcome" />
        )
      }
    >
      <div css={descriptionContainer}>
        <Typography variant="h1" component="p">
          {_t(
            'You can now create as many learning programs as you need. This is the best way to offer customized content to a specific group of learners. For example, you can now assign a course to a class for credit.'
          )}
        </Typography>
      </div>
    </WelcomeBanner>
  );
};

type FirstNameQueryVariables = {};

export default compose<Props, PropsFomCaller>(
  waitForGraphQL<PropsFomCaller, FirstNameQueryType, FirstNameQueryVariables, PropsFromFirstNameQuery>(FirstNameQuery, {
    props: ({ data }) => {
      return {
        firstName: data?.IdVerificationsV1Resource?.me?.elements[0]?.firstName ?? user.get().fullName?.split(/\s/)[0],
      };
    },
  }),
  graphql<Props, ProgramDraftQueryData, ProgramDraftQueryVariables, PropsFromProgramInvitationsQuery>(
    ProgramDraftQuery,
    {
      skip: ({ programId }) => !programId,
      options: ({ programId }) => ({
        variables: { programId },
      }),
      props: ({ data }) => {
        const status = data?.EnterpriseProgramDraftsV1Resource?.get?.status;
        const hasLearners =
          status === ProgramDraftStatus.CONTENT_AND_MEMBERS || status === ProgramDraftStatus.MEMBERS_ONLY;
        return { hasLearners };
      },
    }
  )
)(ContinueProgramSetUpBanner);
